/** @format */

import { Editor } from "@tiptap/react";
import {
  Bold,
  CheckSquare2,
  Code,
  Columns3,
  Heading1,
  Heading2,
  Image as ImageIcon,
  Italic,
  Link2,
  List,
  ListOrdered,
  MessageCircle,
  Redo,
  Rows3,
  Table2,
  Trash2,
  Underline,
  Undo,
} from "lucide-react";
import { FC, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "../ui/button";
import { IconTooltip } from "../ui/IconTooltip";

interface Props {
  editor: Editor | null;
  onImageUpload: () => void;
  /** When provided, shows a toggle to show/hide suggestion highlights in the editor. */
  suggestionsCount?: number;
  showSuggestions?: boolean;
  onToggleSuggestions?: () => void;
  /** When provided, clicking the suggestions button also opens the Document Previewer aside on the Suggestions tab. */
  onOpenSuggestionsPanel?: () => void;
}

const normalizeLinkHref = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

export const EditorToolbar: FC<Props> = ({
  editor,
  onImageUpload,
  suggestionsCount = 0,
  showSuggestions = true,
  onToggleSuggestions,
  onOpenSuggestionsPanel,
}) => {
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkInputValue, setLinkInputValue] = useState("");
  const [linkPopoverRect, setLinkPopoverRect] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const linkInputWrapperRef = useRef<HTMLDivElement>(null);
  const linkPopoverRef = useRef<HTMLDivElement>(null);
  const linkButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inWrapper = linkInputWrapperRef.current?.contains(target) ?? false;
      const inPopover = linkPopoverRef.current?.contains(target) ?? false;
      if (!inWrapper && !inPopover) setShowLinkInput(false);
    };
    if (showLinkInput)
      document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showLinkInput]);

  if (!editor) return null;
  const isInTable = editor.isActive("table");
  const currentLinkHref = editor.getAttributes("link").href;
  const normalizedCurrentLinkHref =
    typeof currentLinkHref === "string" ? currentLinkHref : "";

  const applyLinkFromInput = () => {
    const href = normalizeLinkHref(linkInputValue);
    if (!href) return;
    editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
    setShowLinkInput(false);
    setLinkPopoverRect(null);
  };

  const openLinkPopover = () => {
    setLinkInputValue(normalizedCurrentLinkHref);
    const rect = linkButtonRef.current?.getBoundingClientRect();
    if (rect) setLinkPopoverRect({ top: rect.bottom + 4, left: rect.left });
    setShowLinkInput(true);
  };

  const closeLinkPopover = () => {
    setShowLinkInput(false);
    setLinkPopoverRect(null);
  };

  return (
    <div
      className="sticky top-0 z-10 flex flex-wrap gap-2 p-2 border-b border-gray-700 bg-gray-900 rounded-t-lg"
      onMouseDown={(e) => e.preventDefault()}
    >
      <IconTooltip label="Bold" side="bottom">
        <Button
          className={`btn btn-ghost p-1 ${editor.isActive("bold") ? "bg-gray-700" : ""}`}
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          aria-label="Bold"
        >
          <Bold className="w-4 h-4" />
        </Button>
      </IconTooltip>
      <IconTooltip label="Italic" side="bottom">
        <Button
          className={`btn btn-ghost p-1 ${editor.isActive("italic") ? "bg-gray-700" : ""}`}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          aria-label="Italic"
        >
          <Italic className="w-4 h-4" />
        </Button>
      </IconTooltip>
      <IconTooltip label="Underline" side="bottom">
        <Button
          className={`btn btn-ghost p-1 ${editor.isActive("underline") ? "bg-gray-700" : ""}`}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          aria-label="Underline"
        >
          <Underline className="w-4 h-4" />
        </Button>
      </IconTooltip>
      <IconTooltip label="Link" side="bottom">
        <div className="relative" ref={linkInputWrapperRef}>
          <Button
            ref={linkButtonRef}
            className={`btn btn-ghost p-1 ${editor.isActive("link") ? "bg-gray-700" : ""}`}
            aria-label="Link"
            onClick={() => {
              if (editor.isActive("link")) {
                editor
                  .chain()
                  .focus()
                  .extendMarkRange("link")
                  .unsetLink()
                  .run();
                closeLinkPopover();
                return;
              }
              if (!editor.state.selection.empty) openLinkPopover();
            }}
          >
            <Link2 className="w-4 h-4" />
          </Button>
          {showLinkInput &&
            linkPopoverRect &&
            createPortal(
              <div
                ref={linkPopoverRef}
                className="w-64 rounded-lg border border-gray-600 bg-gray-800 p-2 shadow-lg"
                style={{
                  position: "fixed",
                  top: linkPopoverRect.top,
                  left: linkPopoverRect.left,
                  zIndex: 9999,
                }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <input
                  type="text"
                  value={linkInputValue}
                  onChange={(e) => setLinkInputValue(e.target.value)}
                  placeholder="https://example.com"
                  className="w-full rounded border border-gray-600 bg-gray-900 px-2 py-1 text-xs text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      applyLinkFromInput();
                    }
                    if (e.key === "Escape") {
                      e.preventDefault();
                      closeLinkPopover();
                    }
                  }}
                />
                <div className="mt-2 flex justify-end gap-2">
                  <button
                    type="button"
                    className="rounded px-2 py-1 text-xs text-gray-300 hover:bg-gray-700"
                    onClick={closeLinkPopover}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="rounded px-2 py-1 text-xs text-blue-300 hover:bg-blue-500/20"
                    onClick={applyLinkFromInput}
                  >
                    Apply
                  </button>
                </div>
              </div>,
              document.body,
            )}
        </div>
      </IconTooltip>
      <IconTooltip label="Code block" side="bottom">
        <Button
          aria-label="Code block"
          className={`btn btn-ghost p-1 ${editor.isActive("codeBlock") ? "bg-gray-700" : ""}`}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        >
          <Code className="w-4 h-4" />
        </Button>
      </IconTooltip>
      <IconTooltip label="Bullet list" side="bottom">
        <Button
          className={`btn btn-ghost p-1 ${editor.isActive("bulletList") ? "bg-gray-700" : ""}`}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          aria-label="Bullet list"
        >
          <List className="w-4 h-4" />
        </Button>
      </IconTooltip>
      <IconTooltip label="Ordered list" side="bottom">
        <Button
          className={`btn btn-ghost p-1 ${editor.isActive("orderedList") ? "bg-gray-700" : ""}`}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          aria-label="Ordered list"
        >
          <ListOrdered className="w-4 h-4" />
        </Button>
      </IconTooltip>
      <IconTooltip label="Todo list" side="bottom">
        <Button
          className={`btn btn-ghost p-1 ${editor.isActive("taskList") ? "bg-gray-700" : ""}`}
          onClick={() => editor.chain().focus().toggleTaskList().run()}
          aria-label="Todo list"
        >
          <CheckSquare2 className="w-4 h-4" />
        </Button>
      </IconTooltip>
      <IconTooltip label="Heading 1" side="bottom">
        <Button
          className={`btn btn-ghost p-1 ${editor.isActive("heading", { level: 1 }) ? "bg-gray-700" : ""}`}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
          aria-label="Heading 1"
        >
          <Heading1 className="w-4 h-4" />
        </Button>
      </IconTooltip>
      <IconTooltip label="Heading 2" side="bottom">
        <Button
          className={`btn btn-ghost p-1 ${editor.isActive("heading", { level: 2 }) ? "bg-gray-700" : ""}`}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          aria-label="Heading 2"
        >
          <Heading2 className="w-4 h-4" />
        </Button>
      </IconTooltip>
      <IconTooltip label="Undo" side="bottom">
        <Button
          className="btn btn-ghost p-1"
          aria-label="Undo"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
        >
          <Undo className="w-4 h-4" />
        </Button>
      </IconTooltip>
      <IconTooltip label="Redo" side="bottom">
        <Button
          className="btn btn-ghost p-1"
          aria-label="Redo"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
        >
          <Redo className="w-4 h-4" />
        </Button>
      </IconTooltip>
      <IconTooltip label="Upload image" side="bottom">
        <Button
          className="btn btn-ghost p-1"
          aria-label="Upload image"
          onClick={onImageUpload}
        >
          <ImageIcon className="w-4 h-4" />
        </Button>
      </IconTooltip>
      <div className="flex items-center gap-1 border-l border-gray-700 pl-2">
        <IconTooltip label="Insert table" side="bottom">
          <Button
            className={`btn btn-ghost p-1 ${isInTable ? "bg-gray-700" : ""}`}
            aria-label="Insert table"
            onClick={() =>
              editor
                .chain()
                .focus()
                .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                .run()
            }
          >
            <Table2 className="w-4 h-4" />
          </Button>
        </IconTooltip>
        {isInTable ? (
          <>
            <IconTooltip label="Add row below" side="bottom">
              <Button
                className="btn btn-ghost p-1"
                aria-label="Add row below"
                onClick={() => editor.chain().focus().addRowAfter().run()}
              >
                <Rows3 className="w-4 h-4" />
              </Button>
            </IconTooltip>
            <IconTooltip label="Add column right" side="bottom">
              <Button
                className="btn btn-ghost p-1"
                aria-label="Add column right"
                onClick={() => editor.chain().focus().addColumnAfter().run()}
              >
                <Columns3 className="w-4 h-4" />
              </Button>
            </IconTooltip>
            <IconTooltip label="Delete row" side="bottom">
              <Button
                className="btn btn-ghost p-1"
                aria-label="Delete row"
                onClick={() => editor.chain().focus().deleteRow().run()}
              >
                <Rows3 className="w-4 h-4 text-red-400" />
              </Button>
            </IconTooltip>
            <IconTooltip label="Delete column" side="bottom">
              <Button
                className="btn btn-ghost p-1"
                aria-label="Delete column"
                onClick={() => editor.chain().focus().deleteColumn().run()}
              >
                <Columns3 className="w-4 h-4 text-red-400" />
              </Button>
            </IconTooltip>
            <IconTooltip label="Delete table" side="bottom">
              <Button
                className="btn btn-ghost p-1"
                aria-label="Delete table"
                onClick={() => editor.chain().focus().deleteTable().run()}
              >
                <Trash2 className="w-4 h-4 text-red-400" />
              </Button>
            </IconTooltip>
          </>
        ) : null}
      </div>

      {onToggleSuggestions != null && suggestionsCount > 0 && (
        <div className="flex items-center gap-1 border-l border-gray-700 pl-2 ml-auto">
          <IconTooltip
            label={
              showSuggestions
                ? "Hide feedback"
                : `Show feedback (${suggestionsCount})`
            }
            side="bottom"
          >
            <Button
              type="button"
              className={`flex items-center gap-1.5 rounded px-2 p-1 text-xs font-medium transition-colors ${
                showSuggestions
                  ? "border border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20"
                  : "border border-gray-600 bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-300"
              }`}
              onClick={() => {
                if (showSuggestions) {
                  onToggleSuggestions?.();
                } else {
                  onOpenSuggestionsPanel?.();
                }
              }}
              aria-label={
                showSuggestions ? "Hide feedback" : "Show feedback"
              }
            >
              <MessageCircle className="h-3.5 w-3.5 text-xs" />
            </Button>
          </IconTooltip>
        </div>
      )}
    </div>
  );
};
